/**
 * `/play/:code`: the whole player app. Loads the session for the room, connects, and renders
 * the view it receives: the top bar (phase, round, timer, menu), the phase screen or a tab, the
 * handshake overlay, the paused overlay, announcements and the connection state.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Countdown } from "../components/Countdown.tsx";
import { Toasts } from "../components/Toasts.tsx";
import { PHASE_TITLES } from "../lib/labels.ts";
import { navigate } from "../lib/router.ts";
import { embeddedSession, forgetSession, loadSession } from "../lib/session.ts";
import { connect, disconnect, send, toast, useGame } from "../lib/store.ts";
import { COUNCIL_PHASES, useView } from "../lib/view.ts";
import { useWakeLock } from "../lib/wakeLock.ts";
import type { Outgoing } from "../lib/types.ts";
import { Arrival } from "./Arrival.tsx";
import { Council } from "./Council.tsx";
import { Dusk } from "./Dusk.tsx";
import { End } from "./End.tsx";
import { HandshakeFlow } from "./Handshake.tsx";
import { MenuButton } from "./HostMenu.tsx";
import { Lobby } from "./lobby/Lobby.tsx";
import { Mingle } from "./Mingle.tsx";
import { NotesTab, ReportsTab, RoleTab, TabBar, type TabId } from "./Tabs.tsx";

export function Play({ code }: { code: string }) {
  const session = useMemo(() => loadSession(code), [code]);
  const status = useGame((s) => s.status);
  const detail = useGame((s) => s.statusDetail);
  const hasView = useGame((s) => s.view !== null);

  useEffect(() => {
    if (!session) return;
    connect(session);
    return () => disconnect();
  }, [session]);

  useHarnessBridge();

  if (!session) {
    return (
      <div className="app">
        <main className="main no-tabs stack center">
          <h1>Room {code}</h1>
          <p className="muted">You have not joined this room on this phone.</p>
          <button
            type="button"
            className="btn primary big block"
            onClick={() => navigate(`/join/${code}`)}
          >
            Join room {code}
          </button>
        </main>
      </div>
    );
  }

  if (status === "ended") {
    return (
      <div className="app">
        <main className="main no-tabs stack center" data-testid="session-ended">
          <h1>Session ended</h1>
          <p className="muted">{detail ?? "This session has ended."}</p>
          <button
            type="button"
            className="btn primary big block"
            onClick={() => {
              forgetSession(code);
              navigate(`/join/${code}`);
            }}
          >
            Join room {code} again
          </button>
          <button type="button" className="btn block" onClick={() => navigate("/")}>
            Back to the start
          </button>
        </main>
      </div>
    );
  }

  if (!hasView) {
    return (
      <div className="app">
        <main className="main no-tabs hero">
          <div className="candles">Connecting…</div>
          {status === "reconnecting" ? (
            <p className="muted">The server is not answering yet. Retrying…</p>
          ) : null}
        </main>
        <Toasts />
      </div>
    );
  }
  return <GameShell />;
}

function GameShell() {
  const view = useView();
  const status = useGame((s) => s.status);
  const phase = view.public.phase;
  const [tab, setTab] = useState<TabId>("now");
  const inGame = phase.kind !== "lobby" && phase.kind !== "ended";

  useWakeLock(phase.kind !== "ended");
  useAnnouncements();

  // A new phase always brings everyone back to the phase screen (same moment, every phone).
  const phaseKey = `${phase.kind}:${phase.round}:${phase.ballot}`;
  const lastPhase = useRef(phaseKey);
  useEffect(() => {
    if (lastPhase.current !== phaseKey) {
      lastPhase.current = phaseKey;
      setTab("now");
      window.scrollTo(0, 0);
    }
  }, [phaseKey]);

  const rounds = phase.rounds;
  const sub =
    phase.kind === "lobby"
      ? `${view.public.roster.length} guests`
      : phase.kind === "arrival"
        ? "Before Round 1"
        : phase.kind === "ended"
          ? "Game over"
          : `Round ${phase.round}${rounds ? ` of ${rounds}` : ""}`;

  return (
    <div className="app" data-phase={phase.kind}>
      {status === "reconnecting" || status === "connecting" ? (
        <div className="banner" role="status" data-testid="reconnecting">
          Reconnecting…
        </div>
      ) : null}
      <header className="topbar">
        <div className="phase">
          <div className="phase-title" data-testid="phase-title">
            {PHASE_TITLES[phase.kind]}
            {phase.kind === "ballot" && phase.ballot === 2 ? " 2" : ""}
          </div>
          <div className="phase-sub">
            {sub}
            {phase.kind === "mingle" && phase.closing ? " · finishing handshakes" : ""}
          </div>
        </div>
        <Countdown phase={phase} testId="phase-clock" />
        <MenuButton />
      </header>
      <main className={`main ${inGame ? "" : "no-tabs"}`}>
        {tab === "now" || !inGame ? <PhaseScreen /> : null}
        {inGame && tab === "role" ? <RoleTab /> : null}
        {inGame && tab === "reports" ? <ReportsTab /> : null}
        {inGame && tab === "notes" ? <NotesTab /> : null}
      </main>
      {inGame ? <TabBar tab={tab} onTab={setTab} /> : null}
      {phase.kind === "mingle" ? <HandshakeFlow /> : null}
      {phase.paused ? <PausedOverlay /> : null}
      <Toasts />
    </div>
  );
}

function PhaseScreen() {
  const view = useView();
  const kind = view.public.phase.kind;
  if (kind === "lobby") return <Lobby />;
  if (kind === "arrival") return <Arrival />;
  if (kind === "mingle") return <Mingle />;
  if (kind === "dusk") return <Dusk />;
  if (COUNCIL_PHASES.has(kind)) return <Council />;
  return <End />;
}

function PausedOverlay() {
  const view = useView();
  const last = [...view.public.announcements].reverse().find((a) => a.kind === "paused");
  return (
    <div className="overlay" data-testid="paused">
      <h1>Paused</h1>
      <p className="muted">{last?.text ?? "The game is paused."}</p>
      {view.me.isHost ? (
        <button
          type="button"
          className="btn primary big"
          onClick={() => void send({ type: "resume" })}
        >
          Resume
        </button>
      ) : (
        <p className="faint">The host resumes the game.</p>
      )}
    </div>
  );
}

/** Shows each new public announcement (timer changes, pauses, host changes) as a toast. */
function useAnnouncements(): void {
  const announcements = useGame((s) => s.view?.public.announcements);
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (!announcements) return;
    const latest = announcements.reduce((m, a) => Math.max(m, a.at), 0);
    if (seen.current === null) {
      seen.current = latest;
      return;
    }
    for (const a of announcements) if (a.at > seen.current) toast(a.text, "info", 6_000);
    seen.current = Math.max(seen.current, latest);
  }, [announcements]);
}

/**
 * Dev table harness bridge: when this page runs in a harness tile (an iframe with its session
 * in the URL) on a server with dev tools, the harness may ask it to send a message, e.g. a
 * `devHandshake` after the developer tapped a partner tile. Same origin only.
 */
function useHarnessBridge(): void {
  const devTools = useGame((s) => s.welcome?.devTools === true);
  useEffect(() => {
    if (!devTools || window.parent === window || !embeddedSession()) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== window.parent) return;
      const data = e.data as { type?: string; id?: string; message?: Outgoing } | null;
      if (data?.type !== "harness-send" || !data.message) return;
      void send(data.message).then((ack) =>
        window.parent.postMessage(
          { type: "harness-ack", id: data.id, ack },
          window.location.origin,
        ),
      );
    };
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: "harness-ready" }, window.location.origin);
    return () => window.removeEventListener("message", onMessage);
  }, [devTools]);
}
